import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { jobService } from "../routes/services/AutoGenerateImage/jobService";

const STATUS_ORDER = {
  PENDING: 0,
  RUNNING: 1,
  SUCCESS: 2,
  FAILED: 2,
  CANCELLED: 2
};

const TIMELINE_STEPS = [
  {
    title: "Queued",
    description: "The job was recorded and picked up by the background worker."
  },
  {
    title: "Processing",
    description: "Image creation, AI calls, resizing and storage uploads."
  },
  {
    title: "Finalizing",
    description: "Cleanup, persistence and notifications."
  }
];

export default function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!jobId) {
      setError("Missing job id.");
      setLoading(false);
      return;
    }

    setLoading(true);
    jobService
      .getJobById(jobId)
      .then((data) => {
        if (!data) {
          setError("Job not found.");
        } else {
          setJob(data);
        }
      })
      .catch((err) => {
        setError(err?.message ?? "Failed to load job.");
      })
      .finally(() => setLoading(false));
  }, [jobId]);

  const timeline = useMemo(() => {
    if (!job) {
      return [];
    }

    const numeric = STATUS_ORDER[job.status] ?? 0;

    return TIMELINE_STEPS.map((step, index) => {
      let state = "upcoming";
      if (index < numeric) {
        state = "done";
      } else if (index === numeric) {
        if (job.status === "RUNNING" || job.status === "PENDING") {
          state = "current";
        } else {
          state = "done";
        }
      }

      return {
        ...step,
        state
      };
    });
  }, [job]);

  if (loading) {
    return (
      <div className="container py-4">
        <div className="alert alert-info">Loading job details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">{error}</div>
        <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate("/genimage")}>
          Back to job list
        </button>
      </div>
    );
  }

  if (!job) {
    return null;
  }

  const formattedCreatedAt = job.createdAt
    ? new Date(job.createdAt).toLocaleString()
    : "N/A";

  return (
    <div className="container py-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h4 className="mb-1">Image generation details</h4>
          <p className="text-muted small mb-0">
            Monitoring for background job <strong>{job.name}</strong>.
          </p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate("/genimage")}>
            Back to jobs
          </button>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => navigate("/genimage")}
          >
            Refresh list
          </button>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-xl-8">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-1">Job snapshot</h5>
                  <p className="text-muted small mb-0">Latest status and progress</p>
                </div>
                <span className={`badge bg-${job.status === "SUCCESS" ? "success" : job.status === "FAILED" || job.status === "CANCELLED" ? "danger" : "primary"}`}>
                  {job.status}
                </span>
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between small text-muted mb-1">
                  <span>Progress</span>
                  <span>{job.progress}%</span>
                </div>
                <div className="progress" style={{ height: 8 }}>
                  <div
                    className="progress-bar"
                    style={{
                      width: `${Math.min(100, Math.max(0, job.progress))}%`
                    }}
                  />
                </div>
              </div>

              <p className="small text-muted mb-1">Last message</p>
              <p className="fw-semibold mb-0">
                {job.lastMessage ?? "The job is waiting for more updates."}
              </p>
            </div>
          </div>

          <div className="card shadow-sm border-0 mt-3">
            <div className="card-body">
              <h6 className="mb-3">Pipeline overview</h6>
              <div className="timeline">
                {timeline.map((step) => (
                  <div key={step.title} className="d-flex align-items-start gap-2 mb-3">
                    <span
                      className={`rounded-circle flex-shrink-0 ${
                        step.state === "done"
                          ? "bg-success"
                          : step.state === "current"
                          ? "bg-primary"
                          : "bg-light"
                      }`}
                      style={{ width: 18, height: 18 }}
                    />
                    <div>
                      <div className="fw-semibold mb-1">{step.title}</div>
                      <p className="text-muted small mb-0">{step.description}</p>
                      <span className="text-muted small">
                        {step.state === "current" && "In progress"}
                        {step.state === "done" && "Completed"}
                        {step.state === "upcoming" && "Pending"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-4">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <h6 className="mb-3">Job metadata</h6>
              <dl className="row mb-0 small text-muted">
                <dt className="col-5">Job ID</dt>
                <dd className="col-7 fw-semibold text-dark">{job.id}</dd>
                <dt className="col-5">Job type</dt>
                <dd className="col-7 fw-semibold text-dark">{job.type}</dd>
                <dt className="col-5">Created at</dt>
                <dd className="col-7 fw-semibold text-dark">{formattedCreatedAt}</dd>
                <dt className="col-5">Stored progress</dt>
                <dd className="col-7 fw-semibold text-dark">{job.progress}%</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
